import api from './api';

const STORAGE_KEYS = {
  token: 'token',
  user: 'user',
  referralLink: 'referral_link',
  referralStats: 'referral_stats',
};


function getStoredUser() {
  const raw = localStorage.getItem(STORAGE_KEYS.user);
  return raw ? JSON.parse(raw) : null;
}

function setStoredUser(user) {
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
}

function setToken(token) {
  localStorage.setItem(STORAGE_KEYS.token, token);
}

function getToken() {
  return localStorage.getItem(STORAGE_KEYS.token);
}

export const authService = {
  // Register user with real API
  // Register user with real API
  register: async (userData) => {
    try {
      let apiData = userData;

      // Always use FormData for registration to handle file uploads properly
      if (!(userData instanceof FormData)) {
        apiData = new FormData();
        
        // Add all text fields
        apiData.append('name', userData.name);
        apiData.append('email', userData.email);
        apiData.append('password', userData.password);
        apiData.append('password_confirmation', userData.password_confirmation);
        
        if (userData.phone_number) {
          apiData.append('phone_number', userData.phone_number);
        }
        if (userData.address) {
          apiData.append('address', userData.address);
        }
        if (userData.referal_code) {
          apiData.append('referal_code', userData.referal_code);
        }
        if (userData.gender) {
          apiData.append('gender', userData.gender);
        }
        if (userData.grade_level) {
          apiData.append('grade_level', userData.grade_level);
        }
        if (userData.payment_method) {
          apiData.append('payment_method', userData.payment_method);
        }
        if (userData.transaction_id) {
          apiData.append('transaction_id', userData.transaction_id);
        }
        if (userData.account) {
          apiData.append('account', userData.account);
        }
        
        // Add boolean
        apiData.append('is_agreed_terms', userData.is_agreed_terms ? '1' : '0');
        
        // Add file if present
        if (userData.transaction_image && userData.transaction_image instanceof File) {
          apiData.append('transaction_image', userData.transaction_image);
        }
      }

      console.log('Registering with FormData');
      // Log FormData contents for debugging
      for (let [key, value] of apiData.entries()) {
        console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
      }

      const response = await api.post('/register', apiData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Only store token in localStorage, not user data
      // User data will be stored in context only
      if (response.data.token) {
        setToken(response.data.token);
      }

      return response;
    } catch (error) {
      // Handle API errors
      console.error('Registration error:', error);
      if (error.response) {
        console.error('Error response:', JSON.stringify(error.response.data, null, 2));
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      }
      throw error;
    }
  },

  // Login with real API
  login: async (credentials) => {
    try {
      if (!credentials.username || !credentials.password) {
        const error = new Error('Username and password are required');
        error.response = { data: { message: 'Username and password are required' } };
        throw error;
      }

      // API expects username field, but user enters email
      const loginData = {
        username: credentials.username, // Use username as expected by API
        password: credentials.password,
      };

      console.log('Sending login data:', loginData);
      
      const response = await api.post('/login', loginData);

      console.log('Login response:', response.data);

      // Handle API response structure: { message, user, token, token_type }
      const user = response.data.user || response.data.data;
      const token = response.data.token || response.data.access_token;

      // Only store token in localStorage, not user data
      // User data will be stored in context only
      if (token) {
        setToken(token);
      }

      return { data: { user, token } };
    } catch (error) {
      console.error('Login error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  // Get user profile from API
  getProfile: async () => {
    try {
      const response = await api.get('/profile');

      // Handle different API response structures
      let user;
      if (response.data.user) {
        user = response.data.user;
      } else if (response.data.data) {
        user = response.data.data;
      } else {
        user = response.data;
      }

      // Don't store user data in localStorage
      // User data will be stored in context only
      return { data: user };
    } catch (error) {
      throw error;
    }
  },

  // Update user profile via API
  updateProfile: async (userData) => {
    try {
      const response = await api.put('/profile', userData);

      // Handle different API response structures
      let updated;
      if (response.data.user) {
        updated = response.data.user;
      } else if (response.data.data) {
        updated = response.data.data;
      } else {
        updated = response.data;
      }

      // Don't store user data in localStorage
      // User data will be stored in context only
      return { data: updated };
    } catch (error) {
      throw error;
    }
  },

  // Generate referral link from user's referral code (no API call needed)
  generateReferralLink: async () => {
    try {
      // Get user from localStorage or return empty if not available
      const userStr = localStorage.getItem(STORAGE_KEYS.user);
      let link = '';

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const code = user.referal_code || user.referral_code;
          if (code) {
            link = `${window.location.origin}/register?ref=${code}`;
          }
        } catch (e) {
          // Invalid JSON, ignore
        }
      }

      // Store referral link
      if (link) {
        localStorage.setItem(STORAGE_KEYS.referralLink, link);
      }

      return { data: { link } };
    } catch (error) {
      throw error;
    }
  },

  // Get referral stats from API - uses referrals endpoint to calculate stats
  getReferralStats: async () => {
    try {
      // Use the referrals endpoint to get stats
      const referralService = (await import('./referralService')).referralService;
      const response = await referralService.getAllReferrals();
      const referrals = response.data || [];

      // Calculate stats from referrals data
      const completed = referrals.filter(
        (ref) => ref.status === 'completed' || ref.completed_at !== null
      ).length;
      const pending = referrals.filter(
        (ref) => ref.status === 'pending' || (!ref.completed_at && ref.status !== 'completed')
      ).length;

      const stats = {
        totalReferrals: referrals.length,
        totalEarnings: completed * 10, // Assuming $10 per completed referral
        completed,
        pending,
      };

      // Store referral stats
      if (stats) {
        localStorage.setItem(STORAGE_KEYS.referralStats, JSON.stringify(stats));
      }

      return { data: stats };
    } catch (error) {
      throw error;
    }
  },

  // Forgot password - send reset email
  forgotPassword: async (email) => {
    try {
      // Validate email format
      if (!email || !email.includes('@')) {
        const error = new Error('Valid email is required');
        error.response = { data: { message: 'Valid email is required' } };
        throw error;
      }

      console.log('Sending forgot password request for:', email);
      
      // Try different data formats the API might expect
      const requestData = { email };
      
      const response = await api.post('/forgot-password', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      console.log('Forgot password response:', response.data);
      return response;
    } catch (error) {
      console.error('Forgot password error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // Provide more specific error messages
      if (error.response?.status === 500) {
        error.message = 'Server error occurred. Please try again later or contact support.';
      } else if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors;
        if (validationErrors?.email) {
          error.message = validationErrors.email[0];
        } else {
          error.message = 'Invalid email address.';
        }
      } else if (error.response?.data?.message) {
        error.message = error.response.data.message;
      }
      
      throw error;
    }
  },

  // Reset password with token
  resetPassword: async (token, email, newPassword) => {
    try {
      const response = await api.post('/reset-password', {
        token,
        email,
        password: newPassword,
        password_confirmation: newPassword,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/change-password', {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPassword,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Logout - clear local storage
  logout: async () => {
    try {
      // Optionally call API logout endpoint if available
      // await api.post('/logout');

      // Clear local storage
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.user);
      localStorage.removeItem(STORAGE_KEYS.referralLink);
      localStorage.removeItem(STORAGE_KEYS.referralStats);
      localStorage.removeItem('hasPaid');
    } catch (error) {
      // Even if API call fails, clear local storage
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.user);
      localStorage.removeItem(STORAGE_KEYS.referralLink);
      localStorage.removeItem(STORAGE_KEYS.referralStats);
      localStorage.removeItem('hasPaid');
    }
  },
};
